// #4981 phase 2 (#5268) — the thin accessor over the engine resolver.
//
// The only module of the pair that imports anything. Everything worth testing —
// the pure decision, the latch, the guarded read and both reports — lives in
// `local-database-engine-decisions.ts`; this file holds the ONE production
// resolver and supplies the real reads.
//
// See design-docs/android-local-database-engine.md, "Which engine does a device
// use? (phase 2)".

import { browserLocalStorageOrNull, isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import {
  LOCAL_DATABASE_ENGINE_KEY,
  decideLocalDatabaseEngine,
  SQLITE_READY_BOOTS_KEY,
  SQLITE_READY_BOOTS_TEST_OVERRIDE_KEY,
  LocalDatabaseEngine,
  LocalDatabaseEngineReporter,
  createLocalDatabaseEngineResolver,
} from './local-database-engine-decisions';
import {
  mayDeleteLegacyIndexedDbCopy,
  readSqliteReadyBoots,
} from './sqlite-migration-decisions';
import type { LocalAdapterFaultReporter, LocalCorruptionReporter } from './pouchdb-adapter-capacitor-sqlite/local-corruption-reporter';

/** The one production resolver. Every dep is a function, so nothing here runs at
 *  import time — reading the platform then would answer before the native bridge
 *  is ready. */
const resolver = createLocalDatabaseEngineResolver({
  isIosNative: () => isCapacitorNative() && isPlatformIOS(),
  isNative: () => isCapacitorNative(),

  // **The WHOLE storage lookup must stay inside this one closure**, which is what
  // the resolver wraps in try/catch. A browser with site data blocked throws from
  // the `localStorage` getter itself, before `getItem` is reached, so hoisting
  // `browserLocalStorageOrNull()` out to "simplify" would put that throw outside
  // the catch — where it escapes through `getLocalAdapterOpts` at boot.
  readMarker: () => browserLocalStorageOrNull()?.getItem(LOCAL_DATABASE_ENGINE_KEY) ?? null,
});

/** Which engine does this device keep its PouchDB data in? (#4981 phase 2)
 *  Latched on the first call and unchanged for the life of the page — see
 *  `createLocalDatabaseEngineResolver` for why that matters more than it looks. */
export function getLocalDatabaseEngine(): LocalDatabaseEngine {
  return resolver.get();
}

/** Does this device keep its PouchDB data in SQLite? The one question every storage
 *  site asks — it replaced the scattered `isCapacitorNative() && isPlatformIOS()`
 *  checks that meant this but did not say it. */
export function usesSqliteEngine(): boolean {
  return getLocalDatabaseEngine() === LocalDatabaseEngine.Sqlite;
}

/** Returns PouchDB constructor options for a local database. Uses SQLite on a device
 *  whose engine is SQLite, IndexedDB on every other device — `usesSqliteEngine()` is
 *  the one place that question is answered (#4981 phase 2).
 *
 *  Every local database opens through this ONE function — the user database
 *  (`database-wrapper.ts`), the coach staging database (`coach-exercise-sync.service.ts`)
 *  and the image upload outbox (`image-upload-outbox.service.ts`) — so they all store
 *  in the same backend, and an option added here reaches all of them. It used to be
 *  copied into each of those files (df-7d13bd0e89ed).
 *
 *  `onLocalCorruption` (#4417) is how the SQLite adapter reports an
 *  inconsistency it found in the on-device database — for example a document
 *  whose winning revision has lost its body row, which makes that document
 *  unreadable by reads and unwritable by writes at the same time. Its contract
 *  is `LocalCorruptionReporter`, declared in
 *  `pouchdb-adapter-capacitor-sqlite/local-corruption-reporter.ts`. Callers that have
 *  a LoggingService pass one in; the adapter falls back to console-only when
 *  nobody does.
 *
 *  `onAdapterFault` (df-79401ea69c95) is the other half: an OPERATION of the
 *  adapter that failed, rather than an inconsistency it found in the stored
 *  data. Its first caller is the live changes feed, whose background polls have
 *  no caller to return an error to. Same file, same injection rule. */
export function getLocalAdapterOpts(
  onLocalCorruption?: LocalCorruptionReporter,
  onAdapterFault?: LocalAdapterFaultReporter,
): Record<string, any> {
  if (usesSqliteEngine()) {
    return { adapter: 'capacitor-sqlite', onLocalCorruption, onAdapterFault };
  }
  return {};
}

/**
 * Does the #4401 throwaway-database probe say anything about THIS device's local store?
 *
 * The probe opens an INDEXEDDB database. On a SQLite device it can therefore
 * answer a cheerful "storage works" about a storage system the app does not use,
 * and acting on that offers the user a reset that cannot help and costs them their
 * unsynced data.
 *
 * It moved here from `environment-helpers.ts`, which imports nothing from
 * `services/` — leaving it there would have made an import cycle.
 */
export function probeSpeaksForLocalStore(): boolean {
  return getLocalDatabaseEngine() === LocalDatabaseEngine.IndexedDb;
}

/**
 * May this device DESTROY its old IndexedDB copy? (#4981 phase 2, body replaced by phase 4)
 *
 * Deliberately a DIFFERENT question from `usesSqliteEngine()`, and the legacy cleanup
 * requires both — phase 3 migrates but KEEPS the IndexedDB copy, so the engine alone would
 * destroy the only way back on the boot the copy finished.
 *
 * **The two platforms answer for different reasons, and the difference is the point.**
 *
 * - **iOS** has been on SQLite since long before any of this existed, and the IndexedDB
 *   databases it still carries are leftovers from before that move — dead data by
 *   definition, with nothing to wait for. It answers yes outright, exactly as it did before
 *   phase 4, so nothing about the iOS cleanup changes.
 * - **Android** has just been migrated, and its IndexedDB copy is the pre-migration data —
 *   the only correct copy left if the SQLite one turns out to be wrong in a way the
 *   verification did not catch. So it waits: `REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP`
 *   boots that reached `Ready` on SQLite — not consecutive ones, a boot that wedges earlier
 *   simply does not count — AND an operator's switch
 *   (`AndroidSqliteCleanupEnabled`) still turned on.
 *
 * The config value is passed IN rather than read here, because this module must stay
 * loadable with no Angular and no `ConfigDbService`; `AppComponent` reads it and hands it
 * over. An absent value therefore means the same as a false one — off — which is the safe
 * direction: the cost of a wrong "no" is disk space, and the cost of a wrong "yes" is the
 * user's only remaining correct copy.
 *
 * It lives here rather than beside the cleanup because giving `app/legacy-idb-cleanup.ts`
 * an `environment-helpers` import breaks the two node suites that load it. Why a function
 * rather than a stored marker: the design doc, "The legacy-IndexedDB cleanup needs more
 * than one fact".
 */
export function legacyIdbCleanupPermitted(cleanupEnabledValue?: unknown): boolean {
  if (!isCapacitorNative()) return false;
  if (isPlatformIOS()) return true;

  // Android: the grace period, plus the operator's switch. The counter is read here rather
  // than passed in because it is a plain `localStorage` value this module already reaches
  // for the engine marker.
  //
  // **The whole read is inside the try, and `browserLocalStorageOrNull` is not what makes
  // it safe.** That helper is a bare `typeof` check — evaluating `localStorage` is itself
  // what throws on a browser with site data blocked, so the throw happens INSIDE the helper
  // and travels straight out. This function is documented never to throw and is called with
  // `void` from two sites, so letting one escape would be an unhandled rejection in the
  // middle of a boot. An earlier version of this comment claimed the helper swallowed it,
  // which is the sort of belief that gets this call hoisted somewhere it really matters.
  let readyBoots = 0;
  try {
    const storage = browserLocalStorageOrNull();
    readyBoots = readSqliteReadyBoots(
      storage?.getItem(SQLITE_READY_BOOTS_KEY),
      storage?.getItem(SQLITE_READY_BOOTS_TEST_OVERRIDE_KEY),
    );
  } catch {
    // Zero boots recorded, so the cleanup waits. That is the direction to fail in: waiting
    // costs disk space, deleting early costs the user's only remaining correct copy. The
    // engine accessor already reports an unreadable store at problem tier on this same page
    // load, so this does not report a second time.
  }
  return mayDeleteLegacyIndexedDbCopy(readyBoots, cleanupEnabledValue);
}

/**
 * Which engine this device uses, WITHOUT taking the latch. (#4981 phase 4, #5270)
 *
 * **Use this anywhere that can run before the migration and rollback hooks, and use
 * {@link getLocalDatabaseEngine} everywhere else.** Getting that round the wrong way is not
 * a style slip: `getLocalDatabaseEngine` latches its answer on the first call, and both the
 * migration and the rollback refuse to do anything once the latch is taken — so a single
 * early caller silently turns the whole rollout off, reporting every device as a wiring
 * bug. Telemetry is the dangerous caller, because telemetry runs early and everywhere: the
 * `TelemetryService` constructor builds its environment bag, and any `trackEvent` at all
 * rebuilds it. That is how #5270's first attempt at an engine dimension would have disabled
 * the feature it was added to measure.
 *
 * It answers the same thing the latching accessor would, computed from the same pure rule
 * and the same marker — it simply does not remember it. The one case where the two differ
 * is a page load on which the migration WRITES the marker: the latched answer stays on the
 * engine the session actually used, while this one starts reporting the new engine. For
 * telemetry that is the better answer of the two, and for anything that opens a database it
 * would be the wrong one — which is the other half of why the two exist separately.
 *
 * Never throws. A store that cannot be read at all — a browser with site data blocked
 * throws from the `localStorage` GETTER itself, before `getItem` is reached — answers
 * IndexedDb, which is what an unmigrated device is on.
 */
export function peekLocalDatabaseEngineWithoutLatching(): LocalDatabaseEngine {
  let markerValue: string | null = null;
  try {
    markerValue = browserLocalStorageOrNull()?.getItem(LOCAL_DATABASE_ENGINE_KEY) ?? null;
  } catch {
    // The engine accessor reports an unreadable marker at problem tier on the same page
    // load, so this does not report a second time — and it must not, because the callers
    // are telemetry paths and a report from inside one is how a reporting loop starts.
  }
  return decideLocalDatabaseEngine(
    isCapacitorNative() && isPlatformIOS(), isCapacitorNative(), markerValue);
}

/**
 * Has anything asked the engine question yet on THIS page load? (#4981 phase 3)
 *
 * Only #4981 phase 3's migration asks. It must copy a device's data before the
 * engine answer is latched, because the answer never changes afterwards — see
 * `LocalDatabaseEngineResolver.hasBeenRead` for what goes wrong when it does not.
 *
 * Reading this does NOT take the latch: it reports whether the latch is already
 * taken, and a `get()` here would take it and make every caller's answer `true`.
 */
export function hasLocalDatabaseEngineBeenRead(): boolean {
  return resolver.hasBeenRead();
}

/**
 * Wire the problem-tier reporter. Called ONCE by `AppComponent`'s constructor,
 * the same shape as the `setMyApp(this)` beside it — the engine question is asked
 * from free functions with no `LoggingService` in scope.
 *
 * **Wire it AFTER `this.logging.initialize(...)`**: before that call a report
 * reaches Sentry but loses its analytics half. Anything raised before the wiring
 * is buffered, not dropped.
 */
export function setLocalDatabaseEngineReporter(fn: LocalDatabaseEngineReporter): void {
  resolver.setReporter(fn);
}

/**
 * Drop the latch so a test can exercise both answers.
 *
 * **Production must NEVER call this** — clearing the latch at runtime reopens the
 * data-loss window it exists to close.
 */
export function resetLocalDatabaseEngineLatchForTests(): void {
  resolver.resetForTests();
}
